import React, { useCallback } from 'react';
import { Box, Card, Flex, FormControl, SimpleGrid } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useColorModeVariables } from '../../../components/colorModeVarialbels/ColorModeValues';
import CommonSubmitBtn from '../../../components/commonInput/CommonSubmitBtn';
import { priceStrategyAddEditValidation } from '../../../utils/CommonValidation';
import { responsePriceStrategyView } from 'reactRedux/slices/priceStrategySlice/priceStrategySlice';
import { sideBarRoutes } from 'utils/StaticData';
import useRegions from 'hooks/useRegions';
import {
  AddPriceStrategyData,
  UpdatePriceStrategyData,
  ViewPriceStrategyData,
} from '../../../reactRedux/thunks/priceStrategyThunk/priceStrategyThunk';

const AddEditPriceStrategy = () => {
  const { textColor, brandStars } = useColorModeVariables();
  const { editPriceStrategyId } = useParams();
  const { regions } = useRegions();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, viewPriceStrategyDetails } = useSelector(
    (state) => state.PriceStrategySlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);
  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors },
    control,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(priceStrategyAddEditValidation),
    context: { role: adminDetails?.role },
  });

  const getFirstDefined = (...values) =>
    values.find((value) => value !== undefined && value !== null);

  const onSubmit = (data) => {
    const selectedRegionId =
      data?.regionId?.id || adminDetails?.region?.regionId || null;
    const payload = {
      name: data?.name,
      region: selectedRegionId,
      freeMinutes: Number(data?.freeDuration),
      timeoutDay: Number(data?.daysLimit),
      depositAmount: Number(data?.deposit),
      timeoutAmount: Number(data?.overtimeFees),
      price: Number(data?.price),
      priceTime: Number(data?.priceMinute),
      dailyMaxPrice: Number(data?.maxPrice),
    };
    if (editPriceStrategyId) {
      dispatch(
        UpdatePriceStrategyData(editPriceStrategyId, payload, () => {
          navigate('/admin/price-strategy');
        }),
      );
    } else {
      dispatch(
        AddPriceStrategyData(payload, () => {
          navigate('/admin/price-strategy');
        }),
      );
    }
  };

  useEffect(() => {
    if (editPriceStrategyId) {
      dispatch(ViewPriceStrategyData(editPriceStrategyId));
    }
  }, [dispatch, editPriceStrategyId]);

  useEffect(() => {
    if (viewPriceStrategyDetails?.data && editPriceStrategyId) {
      const strategyData = viewPriceStrategyDetails.data;
      setValue('name', strategyData?.name || '');
      setValue(
        'freeDuration',
        getFirstDefined(strategyData?.freeDuration, strategyData?.freeMinutes),
      );
      setValue(
        'daysLimit',
        getFirstDefined(strategyData?.daysLimit, strategyData?.timeoutDay),
      );
      setValue(
        'deposit',
        getFirstDefined(strategyData?.deposit, strategyData?.depositAmount),
      );
      setValue(
        'overtimeFees',
        getFirstDefined(
          strategyData?.overtimeFees,
          strategyData?.timeoutAmount,
        ),
      );
      setValue('price', strategyData?.price);
      setValue(
        'priceMinute',
        getFirstDefined(strategyData?.priceMinute, strategyData?.priceTime),
      );
      setValue(
        'maxPrice',
        getFirstDefined(strategyData?.maxPrice, strategyData?.dailyMaxPrice),
      );

      // Set region value for admin user
      if (adminDetails?.role === sideBarRoutes?.Admin) {
        const selectedRegionId = getFirstDefined(
          strategyData?.regionId,
          strategyData?.region,
        );
        setValue(
          'regionId',
          regions?.find((region) => region.id === selectedRegionId),
        );
      }
    }
  }, [
    viewPriceStrategyDetails,
    editPriceStrategyId,
    setValue,
    adminDetails,
    regions,
  ]);

  const resetViewPriceStrategyDetails = useCallback(() => {
    dispatch(responsePriceStrategyView({ data: null }));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      resetViewPriceStrategyDetails();
    };
  }, [resetViewPriceStrategyDetails]);

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex direction="column" align="center" justify="center">
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="50px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormControl>
              <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing="24px">
                {/* Region dropdown for admin user */}
                {adminDetails?.role === sideBarRoutes?.Admin && (
                  <Controller
                    name="regionId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="Region"
                        name="regionId"
                        type="select"
                        options={regions}
                        value={value}
                        onChange={onChange}
                        register={register}
                        isRequired={true}
                        error={errors?.regionId}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                        isDisabled={!!editPriceStrategyId}
                      />
                    )}
                  />
                )}

                {/* Name */}
                <CommonInput
                  type="text"
                  name="name"
                  labelText="Name"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Price Strategy Name"
                  register={register('name')}
                  errors={errors?.name}
                />

                <CommonInput
                  type="number"
                  name="price"
                  labelText="Price Per Time Interval"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Price Per Time Interval"
                  register={register('price')}
                  errors={errors?.price}
                />

                <CommonInput
                  type="number"
                  name="priceMinute"
                  labelText="Time Interval"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Time Interval"
                  register={register('priceMinute')}
                  errors={errors?.priceMinute}
                />

                <CommonInput
                  type="number"
                  name="freeDuration"
                  labelText="Free Duration"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Free Duration"
                  register={register('freeDuration')}
                  errors={errors?.freeDuration}
                />

                <CommonInput
                  type="number"
                  name="deposit"
                  labelText="Deposit"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Deposit"
                  register={register('deposit')}
                  errors={errors?.deposit}
                />

                <CommonInput
                  type="number"
                  name="overtimeFees"
                  labelText="Over Time Fees"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Over Time Fees"
                  register={register('overtimeFees')}
                  errors={errors?.overtimeFees}
                />

                <CommonInput
                  type="number"
                  name="maxPrice"
                  labelText="Day Capped Amount"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Day Capped Amount"
                  register={register('maxPrice')}
                  errors={errors?.maxPrice}
                />

                <CommonInput
                  type="number"
                  name="daysLimit"
                  labelText="Days Limit"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Days Limit"
                  register={register('daysLimit')}
                  errors={errors?.daysLimit}
                />
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                <CommonSubmitBtn BTNtext="Submit" isLoading={isLoading} />
              </SimpleGrid>
            </FormControl>
          </form>
        </Card>
      </Flex>
    </Box>
  );
};

export default AddEditPriceStrategy;
